// mecLabels.js — central plain-English label map for MEC method/bucket strings.
//
// Pre-Phase-1 the canonical label switch lived inside MatchRow.jsx's
// `methodLabel()`. Callers either imported that function OR (in a few places)
// duplicated the strings inline. This module is the single source of truth so
// future renames stay consistent.
//
// MatchRow.jsx now imports `labelFor` from here and re-exports `methodLabel`
// for back-compat. New callers should import directly from this module.
//
// The label strings here are the PLAIN-ENGLISH variants — friendlier than the
// raw enum names so bookkeepers / Danish can scan the queue without decoding
// matcher-internal jargon. D-037 / D-038 method names + legacy pre-D-037 names
// are both present.

export const METHOD_LABEL = {
  // ─── Tier 1 ───
  tier_1_po: 'PO matched',
  tier_1_total_mismatch: 'Bill missing or mismatched',

  // ─── Tier 2 ───
  tier_2_heuristic: 'Vendor + amount guess (needs OK)',

  // ─── Ambiguous (multiple candidates / over-billed) ───
  ambiguous: 'QBO over-billed vs Tek',

  // ─── Tier 3 leakage ───
  tier_3_po_unused: 'Refund expected (legacy tag)',
  tier_3_po_not_in_tekmetric: 'Unknown PO# in QBO',
  tier_3_no_po: 'No PO# in QBO memo',

  // ─── Credit Pending family (D-037 + D-038) ───
  credit_pending_po_unused: 'Refund expected',
  credit_pending_zero_cost: 'Refund expected (advisor wrote $0)',
  warranty_credit_pending: 'Warranty refund expected',
  credit_pending_manual: 'Refund expected (manual tag)',
  credit_pending_unreceived_auto_matched: 'Paid for unreceived — auto-matched',
  // D-041 (2026-06-22): core deposit paid upfront, awaiting vendor credit memo.
  core_pending_return: 'Core deposit — awaiting credit',

  // ─── Legacy pre-D-037 method names (still present in old mec_match rows) ───
  pending_credit_warranty_tagged: 'Warranty refund expected (legacy tag)',
  pending_credit_zero_cost_match: 'Refund expected (advisor $0, legacy tag)',
  pending_credit_manual: 'Refund expected (manual, legacy tag)',

  // ─── D-032 credit pairing ───
  credit_paired: 'Credit memo matched',
  credit_unpaired: 'Credit memo (standalone recovery)',

  // ─── D-030 return bucket ───
  return_bucket_pending: 'Return bucket pending',
};

export const BUCKET_LABEL = {
  matched_cogs:   'Used (COGS)',
  wip:            'On shelf (WIP)',
  pending_review: 'Awaiting review',
  pending_credit: 'Refund pending',
  ambiguous:      'Needs investigation',
  leakage:        'Unaccounted',
};

export const TAB_LABEL = {
  matched:       'Matched ROs',
  wip:           'WIP ROs',
  tier1:         'Tier 1 Matches',
  tier2:         'Tier 2 Pending',
  creditPending: 'Credit Pending',
  tier3:         'Tier 3 Leakage',
  ambiguous:     'Ambiguous',
};

/**
 * Return the plain-English label for a matched_method string.
 * Falls back to `fallback` if provided, otherwise echoes the input.
 */
export function labelFor(method, fallback = null) {
  if (method == null) return fallback || '';
  return METHOD_LABEL[method] || fallback || method;
}

/**
 * Return the plain-English label for a bucket string.
 * Falls back to `fallback` if provided, otherwise echoes the input.
 */
export function bucketLabel(bucket, fallback = null) {
  if (bucket == null) return fallback || '';
  return BUCKET_LABEL[bucket] || fallback || bucket;
}

/**
 * Return the plain-English label for a drill-down tab key.
 */
export function tabLabel(tab, fallback = null) {
  if (tab == null) return fallback || '';
  return TAB_LABEL[tab] || fallback || tab;
}

export default {
  METHOD_LABEL,
  BUCKET_LABEL,
  TAB_LABEL,
  labelFor,
  bucketLabel,
  tabLabel,
};
